import React, { useState, useEffect, useRef } from 'react';
import './WorkDetail3.css';
import WorkVisual from './WorkVisual';
import { WORKS, catLabel, mediaUrl, mediaRole, isVideo } from './worksData';
import { loadDisplayWorks } from './store';

// Mesure le ratio naturel (largeur/hauteur) d'une image ou d'une vidéo
const measureRatio = (url) =>
  new Promise((resolve) => {
    if (isVideo(url)) {
      const v = document.createElement('video');
      v.onloadedmetadata = () => resolve(v.videoWidth / v.videoHeight || 16 / 9);
      v.onerror = () => resolve(16 / 9);
      v.src = url;
    } else {
      const img = new Image();
      img.onload = () => resolve(img.naturalWidth / img.naturalHeight || 3 / 2);
      img.onerror = () => resolve(3 / 2);
      img.src = url;
    }
  });

const normalizeMedia = (arr) =>
  (arr || [])
    .map((m) => ({ url: mediaUrl(m), role: mediaRole(m) }))
    .filter((m) => m.url);

export default function WorkDetail3({ work, onBack, onOpenWork }) {
  const [allItems, setAllItems] = useState(WORKS);
  const [flow, setFlow] = useState([]);
  const gooDotRef = useRef(null);
  const gooRectRef = useRef(null);
  const nextBtnRef = useRef(null);
  const lastGoo = useRef({ x: 0, y: 0 });

  // Remonte en haut à chaque ouverture
  useEffect(() => {
    const scroller = document.querySelector('.content-module');
    if (scroller) scroller.scrollTo(0, 0);
  }, [work]);

  // Métaballes sur le bouton "travail suivant" : le curseur fusionne
  // avec le bouton, comme sur le bouton Envoyé de la page Message.
  useEffect(() => {
    const onMove = (e) => {
      const dot = gooDotRef.current;
      const rect = gooRectRef.current;
      const btn = nextBtnRef.current;
      if (!dot || !rect || !btn) return;

      const x = e.clientX;
      const y = e.clientY;
      const r = btn.getBoundingClientRect();
      rect.style.transform = `translate(${r.left}px, ${r.top}px)`;
      rect.style.width = `${r.width}px`;
      rect.style.height = `${r.height}px`;
      // Le flou du filtre goo arrondit les angles : on réduit d'autant
      // l'arrondi de la forme pour qu'elle épouse exactement le bouton.
      const radiusBouton = parseFloat(getComputedStyle(btn).borderTopLeftRadius) || 0;
      rect.style.borderRadius = `${Math.max(0, radiusBouton - 9)}px`;

      const ZONE = 55;
      const inZone =
        x > r.left - ZONE && x < r.right + ZONE &&
        y > r.top - ZONE && y < r.bottom + ZONE;
      rect.style.opacity = inZone ? '1' : '0';
      btn.classList.toggle('goo-hot', inZone);
      document.body.classList.toggle('goo-cursor-active', inZone);

      const dx = x - lastGoo.current.x;
      const dy = y - lastGoo.current.y;
      const speed = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);
      const stretch = Math.min(1 + speed / 20, 2.5);
      dot.style.visibility = inZone ? 'visible' : 'hidden';
      dot.style.transform = `translate(${x + 15}px, ${y + 15}px) rotate(${angle}deg) scale(${stretch}, 1)`;
      lastGoo.current = { x, y };
    };
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      document.body.classList.remove('goo-cursor-active');
    };
  }, [flow, work]);

  // Travaux affichables (exemples non masqués + admin), pour le "travail suivant"
  useEffect(() => {
    loadDisplayWorks().then((liste) => {
      if (liste.length > 0) setAllItems(liste);
    });
  }, []);

  // Règle de composition automatique selon le format naturel de chaque visuel :
  // 16:9 et plus large → grand bloc pleine largeur ; carré / vertical → deux par rangée.
  useEffect(() => {
    if (!work) return undefined;

    const own = normalizeMedia(work.images).slice(1).map((m) => m.url);
    const pool = WORKS.map((w) => w.image).filter((u) => u && u !== work.image);
    const urls = own.length > 0
      ? own
      : [...pool].sort(() => Math.random() - 0.5).slice(0, 8);

    let cancelled = false;
    Promise.all(urls.map((url) => measureRatio(url).then((ratio) => ({ url, ratio })))).then(
      (items) => {
        if (cancelled) return;
        const rows = [];
        let pending = null;
        for (const it of items) {
          if (it.ratio >= 1.45) {
            rows.push({ type: 'full', items: [it] });
          } else if (pending) {
            rows.push({ type: 'pair', items: [pending, it] });
            pending = null;
          } else {
            pending = it;
          }
        }
        if (pending) rows.push({ type: 'pair', items: [pending] });
        setFlow(rows);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [work]);

  // Révélation au scroll
  useEffect(() => {
    const els = document.querySelectorAll('.wd3-reveal');
    if (els.length === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('wd3-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { root: document.querySelector('.content-module'), threshold: 0.12 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [flow, work]);

  if (!work) return null;

  const media = normalizeMedia(
    work.images && work.images.length > 0 ? work.images : (work.image ? [work.image] : [])
  );
  const hero = media[0];
  const category = work.category || catLabel(work.cat);

  const challenge = work.challenge ||
    "Challenge à venir — décrire ici la problématique du client : son contexte, ses contraintes et ce qu'il fallait résoudre.";
  const solution = work.solution ||
    "Solution à venir — décrire ici la réponse du studio : le concept, les choix graphiques et ce qu'ils ont apporté.";
  const about = work.description ||
    "Description à venir — présentation générale du projet, de la collaboration et des résultats.";

  const currentIdx = allItems.findIndex((w) => w.label === work.label);
  const nextWork = allItems[(currentIdx + 1) % allItems.length];

  return (
    <div className="work-detail3">
      <button className="wd3-close" onClick={onBack} aria-label="Fermer">×</button>

      {/* 1. Hero plein écran, titre posé sur l'image */}
      <section className="wd3-hero">
        {hero && <WorkVisual src={hero.url} alt={work.label} className="wd3-hero-visual" />}
        <span className="wd3-hero-scroll">↓</span>
      </section>

      <div className="wd3-container">
        {/* 2. Challenge / Solution : deux cartes bien séparées */}
        <div className="wd3-brief">
          <div className="wd3-card wd3-reveal">
            <span className="wd3-label">01 — Le challenge</span>
            <p className="wd3-card-text">{challenge}</p>
          </div>
          <div className="wd3-card wd3-reveal">
            <span className="wd3-label">02 — La solution</span>
            <p className="wd3-card-text">{solution}</p>
          </div>
        </div>

        {/* 3. Flux de visuels : règle automatique par format */}
        <div className="wd3-flow">
          {flow.map((row, idx) => {
            if (row.type === 'full') {
              return (
                <div
                  key={idx}
                  className="wd3-row wd3-reveal"
                  style={{ aspectRatio: row.items[0].ratio }}
                >
                  <WorkVisual src={row.items[0].url} alt="" className="wd3-cover" />
                </div>
              );
            }
            const pairRatio = Math.max(...row.items.map((m) => m.ratio));
            return (
              <div key={idx} className="wd3-row wd3-row--pair wd3-reveal">
                {row.items.map((m, i) => (
                  <div key={i} style={{ aspectRatio: pairRatio }}>
                    <WorkVisual src={m.url} alt="" className="wd3-cover" />
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* 4. Le projet : carte avec hiérarchie claire */}
        <div className="wd3-card wd3-about wd3-reveal">
          <span className="wd3-label">Le projet</span>
          <p className="wd3-about-text">{about}</p>
          <div className="wd3-about-meta">
            {category && <span>{category}</span>}
            <span>Studio Oblik®</span>
          </div>
        </div>

        {/* 5. Travail suivant : discret, avec effet métaballes */}
        {nextWork && nextWork.label !== work.label && (
          <a
            ref={nextBtnRef}
            href={nextWork.link || '#'}
            className="wd3-next wd3-reveal"
            onClick={(e) => {
              e.preventDefault();
              if (onOpenWork) onOpenWork(nextWork);
            }}
          >
            <span className="wd3-next-label">Travail suivant</span>
            <span className="wd3-next-title">{nextWork.label} →</span>
          </a>
        )}
      </div>

      {/* Couche métaballes : curseur + bouton fusionnent (filtre goo) */}
      {nextWork && nextWork.label !== work.label && (
        <div className="goo-layer" aria-hidden="true">
          <svg width="0" height="0" style={{ position: 'absolute' }}>
            <defs>
              <filter id="wd3-goo-filter">
                <feGaussianBlur in="SourceGraphic" stdDeviation="9" result="blur" />
                <feColorMatrix
                  in="blur"
                  mode="matrix"
                  values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 30 -14"
                />
              </filter>
            </defs>
          </svg>
          <div className="wd3-goo-shapes">
            <div ref={gooDotRef} className="wd3-goo-dot" />
            <div ref={gooRectRef} className="wd3-goo-rect" />
          </div>
        </div>
      )}
    </div>
  );
}
